import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User, LogOut, TrendingUp, Trophy, Link2, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const Header = () => {
  const { user, loading, login, logout } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasExchanges, setHasExchanges] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (user) {
      checkAdminAccess();
      checkExchangeConnections();
    }
  }, [user]);

  const checkAdminAccess = async () => {
    try {
      await axios.get(`${backendUrl}/api/admin/check`, { withCredentials: true });
      setIsAdmin(true);
    } catch (error) {
      setIsAdmin(false);
    }
  };
  
  const checkExchangeConnections = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/exchanges/connections`, { withCredentials: true });
      setHasExchanges(response.data && response.data.length > 0);
    } catch (error) {
      setHasExchanges(false);
    }
  };

  if (loading) {
    return (
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#1a1a1a] border-b border-gray-800 px-4 md:px-6 py-3">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 
            className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#00ff88] to-[#00cc6f] bg-clip-text text-transparent cursor-pointer"
            onClick={() => navigate('/')}
          >
            BuyCrypt Calc
          </h1>
          <div className="w-8 h-8 rounded-full bg-gray-800 animate-pulse" />
        </div>
      </header>
    );
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#1a1a1a] border-b border-gray-800 px-4 md:px-6 py-3">
      <div className="max-w-7xl mx-auto flex justify-between items-center">
        <h1 
          className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#00ff88] to-[#00cc6f] bg-clip-text text-transparent cursor-pointer"
          onClick={() => navigate('/')}
        >
          BuyCrypt Calc
        </h1>

        <div className="flex items-center gap-2 md:gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/leaderboard')}
            className="text-gray-400 hover:text-white hover:bg-gray-800"
          >
            <Trophy className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Лидерборд</span>
          </Button>

          {user && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/exchanges')}
              className="text-gray-400 hover:text-white hover:bg-gray-800"
            >
              <Link2 className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Биржи</span>
            </Button>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 md:h-10 md:w-10 rounded-full">
                  <Avatar className="h-9 w-9 md:h-10 md:w-10">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback className="bg-gradient-to-r from-[#00ff88] to-[#00cc6f] text-black">
                      {user.name?.charAt(0).toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-[#1a1a1a] border-gray-800" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none text-white">{user.name}</p>
                    <p className="text-xs leading-none text-gray-400">{user.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-gray-800" />
                <DropdownMenuItem 
                  onClick={() => navigate('/profile')}
                  className="cursor-pointer text-gray-300 hover:text-white hover:bg-gray-800"
                >
                  <User className="mr-2 h-4 w-4" />
                  <span>Мой профиль</span>
                </DropdownMenuItem>
                {hasExchanges && (
                  <DropdownMenuItem 
                    onClick={() => navigate('/exchanges-dashboard')}
                    className="cursor-pointer text-blue-400 hover:text-white hover:bg-gray-800"
                  >
                    <TrendingUp className="mr-2 h-4 w-4" />
                    <span>Дашборд бирж</span>
                  </DropdownMenuItem>
                )}
                {isAdmin && (
                  <DropdownMenuItem 
                    onClick={() => navigate('/admin')}
                    className="cursor-pointer text-[#00ff88] hover:text-white hover:bg-gray-800"
                  >
                    <Shield className="mr-2 h-4 w-4" />
                    <span>Админка</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-gray-800" />
                <DropdownMenuItem 
                  onClick={logout}
                  className="cursor-pointer text-gray-300 hover:text-white hover:bg-gray-800"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Выйти</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={login}
              className="bg-gradient-to-r from-[#00ff88] to-[#00cc6f] text-black hover:opacity-90 font-semibold"
              size="sm"
            >
              <User className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Войти</span>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
